import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ExpandCollapse } from '@tds/core-expand-collapse';

import FormSummaryRow from '../formSummaryRow';
import { GroupWrapper, GroupTitle } from '../css';
import {
  BACKEND_KEYS,
  additionalEquipment,
  easyAdditionalEquipment,
  oneTimeAdditionalEquipment,
  fieldWinbackFibre,
  valueNewTelusBusinessOrder,
  fieldWinback,
  customerAuthorized,
  customerAuthorizedValcan
} from '../../utils/constant';
import RedeemOffer from '../quoteSummary/componets/RedeemOffer';
import CodeBasedPromotions from './components/codeBasedPromotions';

const userDetailsKey = 'userDetails';
const formSectionKey = 'formSection';
const repeatedFormKey = 'repeatedForm';
const formFieldsGroup = 'formFieldsGroup';
const equipmentGroupSection = 'equipmentGroupSection';

const removePrice = label => {
  if (!label || typeof label !== 'string') return label;
  return label.replace(/\([\D\s]*\$[\d.\s\D]*\)/g, '');
};

const priceClean = label => {
  if (Array.isArray(label)) {
    return label.map(filteredLabel => {
      return removePrice(filteredLabel);
    });
  }
  return label;
};

export const getFieldLabel = (options, fieldValue) => {
  if (!options || !options.length) {
    if (Array.isArray(fieldValue)) {
      return (
        fieldValue &&
        fieldValue.length > 0 &&
        fieldValue.map(field => priceClean(field))
      );
    }
    return priceClean(fieldValue);
  }
  for (let i = 0; i < options.length; i += 1) {
    const option = options[i];
    const fields = option.fields || {};
    const { label, value } = fields || {};
    if (value === fieldValue) {
      return priceClean(label);
    }
  }

  if (Array.isArray(fieldValue) && fieldValue.length === 0) {
    return null;
  }
  return priceClean(fieldValue);
};

const OrderSummary = ({ stepData, orderData }) => {
  const redeemOfferSelection = orderData.redeemOfferData
    ? orderData.redeemOfferData.redeemOfferSelection
    : null;

  const [redeemOfferData, setRedeemOfferData] = useState(redeemOfferSelection);

  const summaryStepsData = useMemo(() => {
    const stepsData = (stepData && [...stepData]) || [];
    stepsData.splice(stepsData.length - 1, 1);
    return stepsData;
  }, stepData);

  const groupSummary = (
    fieldNamesArray,
    formStepFieldsArray,
    groupOrderData
  ) => {
    return formStepFieldsArray.map((formStepFields, index) => {
      if (!formStepFields) return null;
      const nonEmptyFields =
        formStepFields &&
        formStepFields.filter(currentField => {
          const {
            fields: { backendKey }
          } = currentField || {};
          const isObject = typeof groupOrderData[backendKey] === 'object';
          const isArray = Array.isArray(groupOrderData[backendKey]);
          if (isObject && !isArray) {
            return !!groupOrderData[backendKey].value;
          }
          if (isArray && groupOrderData[backendKey].length) {
            return true;
          }
          return !!groupOrderData[backendKey];
        });

      if (!nonEmptyFields || !nonEmptyFields.length) return null;
      const setTitle = label => {
        if (label === additionalEquipment) {
          return `${label} (${oneTimeAdditionalEquipment})`;
        }
        if (label === easyAdditionalEquipment) {
          return `${additionalEquipment} (${label})`;
        }
        return label;
      };
      return (
        <GroupWrapper style={{ marginBottom: '1em' }}>
          <GroupTitle style={{ marginBottom: '1em' }}>
            {setTitle(fieldNamesArray[index])}
          </GroupTitle>
          {nonEmptyFields &&
            nonEmptyFields.map(({ fields: { backendKey, label, options } }) => {
              let fieldValue = '';
              const isArray = Array.isArray(groupOrderData[backendKey]);
              const isObject = typeof groupOrderData[backendKey] === 'object';
              if (isObject && !isArray) {
                fieldValue = priceClean(groupOrderData[backendKey].value);
              } else if (isArray) {
                fieldValue = groupOrderData[backendKey]
                  .join(', ')
                  .split(',')
                  .map(item => priceClean(item))
                  .join(',');
              } else {
                fieldValue = priceClean(groupOrderData[backendKey]);
              }
              const labelLogic =
                fieldNamesArray[index] &&
                fieldNamesArray[index] === additionalEquipment
                  ? priceClean(label)
                  : priceClean(label);
              return (
                <FormSummaryRow
                  label={labelLogic}
                  value={getFieldLabel(options, fieldValue)}
                  isDollarRemove
                />
              );
            })}
        </GroupWrapper>
      );
    });
  };

  const handleFormSummaryRow = ({
    fieldLabel,
    fieldBackendKey,
    fieldName,
    formStepFields,
    contentfulId,
    formTitle,
    options: mainOptions,
    appearsAfterValue,
    appearsAfterField,
    easyPaymentFields,
    easyPaymentTitle,
    fiveYearEasyPaymentTitle,
    fiveYearEasyPaymentFields
  }) => {
    if (
      (fieldName === fieldWinbackFibre || fieldName === fieldWinback) &&
      orderData.orderType !== valueNewTelusBusinessOrder
    )
      return null;
    if (contentfulId === userDetailsKey) {
      return groupSummary([fieldName], [formStepFields], orderData);
    }
    if (contentfulId === formFieldsGroup) {
      return groupSummary([fieldName], [formStepFields], orderData);
    }
    if (contentfulId === equipmentGroupSection) {
      if (orderData[fieldName] && Object.keys(orderData[fieldName]).length) {
        let conditionField = null;
        let conditionValue = null;
        if (appearsAfterField && appearsAfterField.length) {
          const [
            {
              fields: { backendKey: appearsBackendKey }
            }
          ] = appearsAfterField || [{}];
          if (appearsBackendKey) {
            conditionField = appearsBackendKey;
          }
        }
        if (appearsAfterValue && appearsAfterValue.length) {
          const [
            {
              fields: { value: appearsValue }
            }
          ] = appearsAfterValue || [{}];
          if (appearsValue) {
            conditionValue = appearsValue;
          }
        }
        if (
          conditionField &&
          conditionValue &&
          orderData[conditionField] !== conditionValue
        ) {
          return null;
        }
        const paymentsFields = easyPaymentFields || [];
        return groupSummary(
          [formTitle, easyPaymentTitle,fiveYearEasyPaymentTitle],
          [formStepFields, paymentsFields,fiveYearEasyPaymentFields],
          orderData[fieldName]
        );
      }
      return null;
    }
    if (contentfulId === formSectionKey) {
      return (
        formStepFields &&
        formStepFields.length > 0 &&
        formStepFields.map(item => {
          const {
            fields: {
              label,
              backendKey,
              name: sectionFieldName,
              fields: groupFields,
              formFields: sectionFormFields,
              title,
              formTitle: sectionFormTitle
            },
            sys: {
              contentType: {
                sys: { id: fieldContentfulId }
              }
            }
          } = item || {};
          const rowObj = {
            fieldLabel: label,
            fieldBackendKey: backendKey,
            fieldName: sectionFieldName || title,
            formStepFields: groupFields || sectionFormFields,
            contentfulId: fieldContentfulId,
            formTitle: title || sectionFormTitle
          };
          return handleFormSummaryRow(rowObj);
        })
      );
    }
    if (contentfulId === repeatedFormKey) {
      return (
        orderData[fieldName] &&
        orderData[fieldName].map((row, idx) =>
          groupSummary([`${formTitle} ${idx + 1}`], [formStepFields], row)
        )
      );
    }
    const fieldKey =
      (orderData[fieldBackendKey] && fieldBackendKey) || fieldName;
    return (
      <FormSummaryRow
        label={fieldLabel}
        value={getFieldLabel(mainOptions, orderData[fieldKey])}
        convertBoolValueToString={
          fieldBackendKey === customerAuthorized ||
          fieldBackendKey === customerAuthorizedValcan
        }
        isDollarRemove
      />
    );
  };

  const handleOfficePhone = (stepName, formStepFields) => {
    return (
      orderData &&
      orderData.officePhoneData.map((mainLine, idx) => {
        return (
          <>
            {groupSummary(
              [`${stepName} ${idx + 1}`],
              [formStepFields],
              mainLine
            )}
            {mainLine &&
              mainLine.ovLines &&
              mainLine.ovLines.map((ovLine, ovIdx) =>
                groupSummary(
                  [`${stepName} ${idx + 1}.${ovIdx + 1}`],
                  [formStepFields],
                  ovLine
                )
              )}
          </>
        );
      })
    );
  };

  return (
    <>
      <ExpandCollapse tag="h2">
        {summaryStepsData &&
          summaryStepsData.map(
            ({
              fields: {
                name: stepName,
                backendKey: stepBackendKey,
                formStepFields
              }
            }) => (
              <ExpandCollapse.Panel
                id={stepBackendKey}
                header={stepName}
                key={stepBackendKey}
              >
                <>
                  {stepBackendKey === BACKEND_KEYS.officePhone &&
                    handleOfficePhone(stepName, formStepFields)}
                  {stepBackendKey !== BACKEND_KEYS.officePhone &&
                    formStepFields &&
                    formStepFields.map(
                      ({
                        fields: {
                          label: fieldLabel,
                          outputLabel,
                          backendKey: fieldBackendKey,
                          formStepFields: subStepFields,
                          easyPaymentFields,
                          easyPaymentTitle,
                            fiveYearEasyPaymentFields,fiveYearEasyPaymentTitle,
                          formFields: repeatedGroupFields,
                          appearsAfterField,
                          appearsAfterValue,
                          fields: groupFields,
                          name: fieldName,
                          title: groupTitle,
                          formTitle,
                          options
                        },
                        sys: {
                          contentType: {
                            sys: { id: contentfulId }
                          }
                        }
                      }) => (
                        <>
                          {handleFormSummaryRow({
                            fieldLabel: outputLabel || fieldLabel,
                            fieldBackendKey,
                            formStepFields:
                              subStepFields ||
                              repeatedGroupFields ||
                              groupFields,
                            fieldName: fieldName || groupTitle,
                            contentfulId,
                            formTitle: formTitle || groupTitle,
                            options,
                            appearsAfterField,
                            appearsAfterValue,
                            easyPaymentFields,
                            easyPaymentTitle,
                                fiveYearEasyPaymentFields,
                                fiveYearEasyPaymentTitle
                          })}
                        </>
                      )
                    )}
                </>
              </ExpandCollapse.Panel>
            )
          )}
        {/* Panel to show code based promotions */}
        {redeemOfferData && (
          <ExpandCollapse.Panel
            id="codeBasedPromotions"
            header={'Coupon Based Discounts'}
          >
            <CodeBasedPromotions orderData={orderData} />
          </ExpandCollapse.Panel>
        )}
      </ExpandCollapse>
    </>
  );
};

OrderSummary.propTypes = {
  stepData: PropTypes.objectOf(PropTypes.any).isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired
};

export default OrderSummary;
